/**
 * Whether the current session can read tenant data yet (wave B polish: a
 * new employee's home showed 403s from announcements / birthdays — the
 * home route's hover/navigation prefetch fired while the session was still
 * on the temporary password (403 password_change_required) or had not
 * picked a workspace yet, before the auth gate redirected).
 *
 * The AuthProvider records every /me it loads; route loaders that prefetch
 * tenant data check sessionReady() first and otherwise leave the fetch to
 * the page's own queries, which only mount behind the auth gate.
 * Dependency-free for `npm run test:unit`.
 */

export interface SessionFacts {
  user_id: number;
  tenant_id?: number;
  must_change_password?: boolean;
  /** /me.features — lets a loader skip a prefetch the plan doesn't cover. */
  features?: readonly string[];
}

/** A tenant membership is active and nothing must happen first. */
export function isSessionReady(me: SessionFacts | null | undefined): boolean {
  return !!me && me.user_id > 0 && !!me.tenant_id && me.must_change_password !== true;
}

let ready = false;
let features: ReadonlySet<string> = new Set();

/** Called by the AuthProvider whenever /me changes. */
export function recordSession(me: SessionFacts | null | undefined): void {
  ready = isSessionReady(me);
  features = new Set(ready ? (me?.features ?? []) : []);
}

/** True once the last /me was a ready tenant session. */
export function sessionReady(): boolean {
  return ready;
}

/**
 * True once the last /me was a ready tenant session whose plan carries
 * `feature`. A route loader prefetching a plan-gated endpoint checks this
 * first: a 403 upgrade_required from a prefetch would bounce the browser to
 * Billing on hover (POSH only, migration 162).
 */
export function sessionHasFeature(feature: string): boolean {
  return ready && features.has(feature);
}
